import { IconType } from '@components/Icon';
import { ItemMenu } from '../abstract/ItemMenu';
import { LeftMenuAttributes } from 'chaika-test-attributes';
import type { TranslationValues } from '@chaika/i18n/types';
import { WALK_IN_NAV_LINK } from '@modules/walkIn/routes/walkInRoutes';
import { DATA_ISOLATION_NAV_LINK } from '@modules/dataIsolation/routes/dataIsolationRoutes';
import { assertIsDefined } from '@utils/assertIsDefined';

export interface NavigationItemParams {
    title: TranslationValues;
    link: string;
    blank?: boolean;
    dataTest: string;
    iconType: IconType;
    subItems?: NavigationItemParams[];
}

const navigationItems: NavigationItemParams[] = [
    {
        title: 'dashboard',
        link: '/dashboard',
        iconType: IconType.DASHBOARDS,
        dataTest: LeftMenuAttributes.Dashboard,
    },
    {
        title: 'patient',
        link: '/patient',
        iconType: IconType.PATIENT,
        dataTest: LeftMenuAttributes.Patient,
    },
    {
        title: 'schedule',
        link: '/schedule',
        iconType: IconType.NEW_SCHEDULE,
        dataTest: LeftMenuAttributes.Schedule,
    },
    {
        title: 'billing',
        link: '/billing',
        iconType: IconType.PAYMENT,
        dataTest: LeftMenuAttributes.Billing,
    },
    {
        title: 'walkIn',
        link: WALK_IN_NAV_LINK,
        iconType: IconType.PULSE,
        dataTest: LeftMenuAttributes.WalkIn,
    },
    {
        title: 'references',
        blank: true,
        link: 'https://drive.google.com/drive/folders/1t9lUs4kI0_G5MQ18d4BjnuB5EKWd4bLx?usp=sharing',
        iconType: IconType.DOCUMENT,
        dataTest: LeftMenuAttributes.Certificate,
    },
    {
        title: 'priceList',
        link: '/price-list-view',
        iconType: IconType.PRICE_LIST,
        dataTest: LeftMenuAttributes.PriceListView,
    },
    {
        title: 'clinics',
        link: '/clinics',
        iconType: IconType.LOCATION,
        dataTest: LeftMenuAttributes.Clinics,
    },
];

const navigationDirectorItems: NavigationItemParams[] = [
    {
        title: 'Админ панель',
        link: '/template',
        iconType: IconType.SETTING,
        dataTest: LeftMenuAttributes.Setting,
        subItems: [
            {
                title: 'entryTypes',
                link: '/template',
                iconType: IconType.ENTRY_TYPE,
                dataTest: LeftMenuAttributes.Template,
            },
            {
                title: 'notification',
                link: '/notification',
                iconType: IconType.ENTRY_TYPE,
                dataTest: LeftMenuAttributes.Notification,
            },
            {
                title: 'users',
                link: '/user',
                iconType: IconType.USER,
                dataTest: LeftMenuAttributes.User,
            },
            {
                title: 'organizations',
                link: '/organization',
                iconType: IconType.ORGANIZATION,
                dataTest: LeftMenuAttributes.Organization,
            },
            {
                title: 'servicesAndPrices',
                link: '/services',
                iconType: IconType.SERVICE,
                dataTest: LeftMenuAttributes.Services,
            },
            {
                title: 'plans',
                link: '/plan',
                iconType: IconType.PLAN,
                dataTest: LeftMenuAttributes.Plan,
            },
            {
                title: 'laboratory',
                link: '/laboratory-admin',
                iconType: IconType.LAB,
                dataTest: LeftMenuAttributes.LaboratoryAdmin,
            },
            {
                title: 'uiTranslate',
                link: '/configuration/translations',
                iconType: IconType.SETTING,
                dataTest: LeftMenuAttributes.UiTranslations,
            },
            {
                title: 'prompts',
                link: '/prompts',
                iconType: IconType.WINDOW_TEXT,
                dataTest: LeftMenuAttributes.Prompts,
            },
        ],
    },
];

const directorSubItems = navigationDirectorItems[0].subItems;
assertIsDefined(directorSubItems, 'Director navigation root item is declared with sub items');

const navigationRootAccessItems: NavigationItemParams[] = [
    {
        ...navigationDirectorItems[0],
        subItems: [
            ...directorSubItems,
            {
                title: 'featureToggle',
                link: '/configuration',
                iconType: IconType.SETTING,
                dataTest: LeftMenuAttributes.FeatureFlag,
            },
            {
                title: 'attachment',
                link: '/attachment',
                iconType: IconType.SETTING,
                dataTest: LeftMenuAttributes.AttachmentStatus,
            },
            {
                title: 'financeConfiguration',
                link: '/finance/configuration',
                iconType: IconType.SETTING,
                dataTest: LeftMenuAttributes.FinanceConfiguration,
            },
            {
                title: 'dataIsolation',
                link: DATA_ISOLATION_NAV_LINK,
                iconType: IconType.SHIELD_LOCK,
                dataTest: LeftMenuAttributes.DataIsolation,
            },
        ],
    },
];

const navigationAssistantItems: NavigationItemParams[] = [
    {
        title: 'assistant',
        link: '/user',
        iconType: IconType.SETTING,
        dataTest: LeftMenuAttributes.AssistanceSetting,
        subItems: [
            {
                title: 'users',
                link: '/user',
                iconType: IconType.USER,
                dataTest: LeftMenuAttributes.User,
            },
        ],
    },
];

const navigationAccountantItems: NavigationItemParams[] = [
    {
        title: 'accounting',
        link: '/accounting',
        iconType: IconType.ACCOUNTING,
        dataTest: LeftMenuAttributes.Accounting,
    },
];

const navigationNotificationItems: NavigationItemParams[] = [
    {
        title: 'notification',
        link: '/notification',
        iconType: IconType.ENTRY_TYPE,
        dataTest: LeftMenuAttributes.Notification,
    },
];

export class LeftMenu extends ItemMenu<NavigationItemParams> {
    constructor() {
        super();

        this.items = navigationItems;
    }

    public get directorItems(): NavigationItemParams[] {
        return navigationDirectorItems;
    }

    public get rootAccessItems(): NavigationItemParams[] {
        return navigationRootAccessItems;
    }

    public get assistantItems(): NavigationItemParams[] {
        return navigationAssistantItems;
    }

    public get accountantItems(): NavigationItemParams[] {
        return navigationAccountantItems;
    }

    public get notificationItems(): NavigationItemParams[] {
        return navigationNotificationItems;
    }

    public getDocrotItem(doctorId: string): NavigationItemParams[] {
        return [
            {
                title: 'businessChaika',
                blank: true,
                link: `https://business.chaika.com/?user_id=${doctorId}`,
                iconType: IconType.ENTRY_TYPE,
                dataTest: LeftMenuAttributes.BC,
            },
        ];
    }
}
